import { useState, useEffect } from 'react';
import { useSimulationStore } from '../../stores/simulationStore';
import { useInstanceStore } from '../../stores/instanceStore';
import type {
  TransportType,
  SignalKConfig,
  Nmea0183Config,
  UdpBroadcastConfig,
  TcpServerConfig,
} from '../../types';

const TRANSPORT_SHORT: Record<TransportType, string> = {
  none: 'Offline',
  tcpServer: 'TCP Srv',
  nmea0183: 'NMEA',
  udpBroadcast: 'UDP',
  signalk: 'SK',
};

const TRANSPORT_DESC: Record<Exclude<TransportType, 'none'>, string> = {
  tcpServer: 'Accepts TCP connections and broadcasts NMEA0183 sentences to all connected clients',
  nmea0183: 'Connects to a TCP server (e.g. Signal-K) and sends NMEA0183 sentences',
  udpBroadcast: 'Broadcasts NMEA0183 sentences to all devices on the local network',
  signalk: 'Connects to a Signal-K server via WebSocket and sends delta updates',
};

// Transport config forms
function TcpServerConfig({
  onConnect,
  onDisconnect,
  isActive,
}: {
  onConnect: (config: TcpServerConfig) => void;
  onDisconnect: () => void;
  isActive: boolean;
}) {
  const { tcpServerStatus, tcpServerError, tcpServerMessagesSent, tcpServerClientsConnected } =
    useSimulationStore();
  const activeInstance = useInstanceStore((s) => s.activeInstance());
  const defaultPort = String(activeInstance?.tcp_port ?? 10110);
  const [portStr, setPortStr] = useState(defaultPort);
  const port = parseInt(portStr) || 10110;

  // Update port when active instance changes
  useEffect(() => {
    setPortStr(defaultPort);
  }, [defaultPort]);

  return (
    <>
      <div>
        <label className="label">Server Port</label>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          className="input text-sm"
          value={portStr}
          onChange={(e) => setPortStr(e.target.value.replace(/[^0-9]/g, ''))}
          disabled={isActive}
        />
      </div>
      <StatusDisplay
        status={tcpServerStatus === 'listening' ? 'connected' : tcpServerStatus === 'stopped' ? 'disconnected' : 'error'}
        error={tcpServerError}
        extra={
          tcpServerStatus === 'listening' ? (
            <div className="text-xs text-gray-400 space-y-0.5">
              <div>Clients: {tcpServerClientsConnected}</div>
              <div>Sent: {tcpServerMessagesSent}</div>
            </div>
          ) : null
        }
      />
      <ConnectButton
        isActive={isActive}
        onConnect={() => onConnect({ port })}
        onDisconnect={onDisconnect}
        connectLabel="Start Server"
        disconnectLabel="Stop Server"
      />
    </>
  );
}

function Nmea0183ConfigForm({
  onConnect,
  onDisconnect,
  isActive,
}: {
  onConnect: (config: Nmea0183Config) => void;
  onDisconnect: () => void;
  isActive: boolean;
}) {
  const { nmea0183Status, nmea0183Error, nmea0183MessagesSent } = useSimulationStore();
  const [host, setHost] = useState('nmea.vortexnav.com');
  const [portStr, setPortStr] = useState('10110');
  const port = parseInt(portStr) || 10110;
  const [forceIpv4, setForceIpv4] = useState(false);

  return (
    <>
      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2">
          <label className="label">Host</label>
          <input
            type="text"
            className="input text-sm"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            disabled={isActive}
          />
        </div>
        <div>
          <label className="label">Port</label>
          <input
            type="number"
            className="input text-sm"
            value={portStr}
            onChange={(e) => setPortStr(e.target.value)}
            disabled={isActive}
          />
        </div>
      </div>
      <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
        <input
          type="checkbox"
          checked={forceIpv4}
          onChange={(e) => setForceIpv4(e.target.checked)}
          disabled={isActive}
          className="accent-blue-500"
        />
        Force IPv4
      </label>
      <StatusDisplay
        status={nmea0183Status}
        error={nmea0183Error}
        extra={
          nmea0183Status === 'connected' ? (
            <div className="text-xs text-gray-400">Sent: {nmea0183MessagesSent}</div>
          ) : null
        }
      />
      <ConnectButton
        isActive={isActive}
        connecting={nmea0183Status === 'connecting'}
        onConnect={() => onConnect({ host, port, auto_reconnect: true, reconnect_interval_ms: 5000, force_ipv4: forceIpv4 })}
        onDisconnect={onDisconnect}
      />
    </>
  );
}

function UdpBroadcastConfigForm({
  onConnect,
  onDisconnect,
  isActive,
}: {
  onConnect: (config: UdpBroadcastConfig) => void;
  onDisconnect: () => void;
  isActive: boolean;
}) {
  const { udpBroadcastStatus, udpBroadcastError, udpBroadcastMessagesSent } = useSimulationStore();
  const [address, setAddress] = useState('192.168.1.255');
  const [portStr, setPortStr] = useState('10110');
  const port = parseInt(portStr) || 10110;

  return (
    <>
      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2">
          <label className="label">Broadcast Address</label>
          <input
            type="text"
            className="input text-sm"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            disabled={isActive}
          />
        </div>
        <div>
          <label className="label">Port</label>
          <input
            type="number"
            className="input text-sm"
            value={portStr}
            onChange={(e) => setPortStr(e.target.value)}
            disabled={isActive}
          />
        </div>
      </div>
      <StatusDisplay
        status={udpBroadcastStatus}
        error={udpBroadcastError}
        extra={
          udpBroadcastStatus === 'connected' ? (
            <div className="text-xs text-gray-400">Sent: {udpBroadcastMessagesSent}</div>
          ) : null
        }
      />
      <ConnectButton
        isActive={isActive}
        onConnect={() => onConnect({ address, port })}
        onDisconnect={onDisconnect}
        connectLabel="Start Broadcast"
        disconnectLabel="Stop"
      />
    </>
  );
}

function SignalKConfigForm({
  onConnect,
  onDisconnect,
  isActive,
}: {
  onConnect: (config: SignalKConfig) => void;
  onDisconnect: () => void;
  isActive: boolean;
}) {
  const { signalkStatus, signalkError, signalkMessagesSent } = useSimulationStore();
  const [serverUrl, setServerUrl] = useState(
    'ws://nmea.vortexnav.com:3000/signalk/v1/stream?subscribe=none'
  );

  return (
    <>
      <div>
        <label className="label">Server URL</label>
        <input
          type="text"
          className="input text-sm"
          value={serverUrl}
          onChange={(e) => setServerUrl(e.target.value)}
          disabled={isActive}
        />
      </div>
      <StatusDisplay
        status={signalkStatus}
        error={signalkError}
        extra={
          signalkStatus === 'connected' ? (
            <div className="text-xs text-gray-400">Sent: {signalkMessagesSent}</div>
          ) : null
        }
      />
      <ConnectButton
        isActive={isActive}
        connecting={signalkStatus === 'connecting'}
        onConnect={() =>
          onConnect({
            server_url: serverUrl,
            vessel_uuid: null,
            auto_reconnect: true,
            reconnect_interval_ms: 5000,
          })
        }
        onDisconnect={onDisconnect}
      />
    </>
  );
}

// Shared status display
function StatusDisplay({
  status,
  error,
  extra,
}: {
  status: string;
  error: string | null;
  extra?: React.ReactNode;
}) {
  const statusColors: Record<string, string> = {
    disconnected: 'status-disconnected',
    connecting: 'status-connecting',
    connected: 'status-connected',
    error: 'status-error',
    stopped: 'status-disconnected',
    listening: 'status-connected',
  };

  const statusLabels: Record<string, string> = {
    disconnected: 'Disconnected',
    connecting: 'Connecting...',
    connected: 'Connected',
    error: 'Error',
    stopped: 'Stopped',
    listening: 'Listening',
  };

  return (
    <>
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-400">Status:</span>
        <span className="flex items-center">
          <span className={`status-dot ${statusColors[status] || 'status-disconnected'}`} />
          <span className="text-gray-200">{statusLabels[status] || status}</span>
        </span>
      </div>
      {error && (
        <div className="text-xs text-red-400 bg-red-900/20 p-2 rounded">{error}</div>
      )}
      {extra}
    </>
  );
}

// Shared connect/disconnect button
function ConnectButton({
  isActive,
  connecting,
  onConnect,
  onDisconnect,
  connectLabel = 'Connect',
  disconnectLabel = 'Disconnect',
}: {
  isActive: boolean;
  connecting?: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
  connectLabel?: string;
  disconnectLabel?: string;
}) {
  return (
    <div className="flex justify-end pt-1">
      {isActive ? (
        <button className="btn btn-danger text-sm" onClick={onDisconnect}>
          {disconnectLabel}
        </button>
      ) : (
        <button
          className="btn btn-primary text-sm"
          onClick={onConnect}
          disabled={connecting}
        >
          {connecting ? 'Connecting...' : connectLabel}
        </button>
      )}
    </div>
  );
}

export default function ConnectionStatus() {
  const {
    activeTransport,
    setActiveTransport,
    disconnectAllTransports,
    // Transport actions
    connectSignalK,
    connectNmea0183,
    connectUdpBroadcast,
    startTcpServer,
    // Status for header dot
    signalkStatus,
    nmea0183Status,
    udpBroadcastStatus,
    tcpServerStatus,
    tcpServerClientsConnected,
    signalkMessagesSent,
    nmea0183MessagesSent,
    udpBroadcastMessagesSent,
    tcpServerMessagesSent,
  } = useSimulationStore();

  const [showPanel, setShowPanel] = useState(false);

  // Determine if the active transport is currently connected (not just connecting)
  const isTransportActive = (): boolean => {
    switch (activeTransport) {
      case 'signalk': return signalkStatus === 'connected';
      case 'nmea0183': return nmea0183Status === 'connected';
      case 'udpBroadcast': return udpBroadcastStatus === 'connected';
      case 'tcpServer': return tcpServerStatus === 'listening';
      default: return false;
    }
  };

  const isActive = isTransportActive();

  // Get the status color for the header button
  const getHeaderStatusColor = (): string => {
    if (activeTransport === 'none') return 'status-disconnected';
    if (isActive) return 'status-connected';
    // Check for error state
    switch (activeTransport) {
      case 'signalk': return signalkStatus === 'error' ? 'status-error' : 'status-disconnected';
      case 'nmea0183': return nmea0183Status === 'error' ? 'status-error' : 'status-disconnected';
      case 'udpBroadcast': return udpBroadcastStatus === 'error' ? 'status-error' : 'status-disconnected';
      case 'tcpServer': return tcpServerStatus === 'error' ? 'status-error' : 'status-disconnected';
      default: return 'status-disconnected';
    }
  };

  // Get message count for header
  const getMessageCount = (): number => {
    switch (activeTransport) {
      case 'signalk': return signalkMessagesSent;
      case 'nmea0183': return nmea0183MessagesSent;
      case 'udpBroadcast': return udpBroadcastMessagesSent;
      case 'tcpServer': return tcpServerMessagesSent;
      default: return 0;
    }
  };

  // Switch transport type (disconnect current first)
  const handleTransportSelect = async (transport: TransportType) => {
    if (transport === activeTransport) return;
    await disconnectAllTransports();
    setActiveTransport(transport);
  };

  // Connect the currently selected transport
  const handleConnect = async (config: unknown) => {
    switch (activeTransport) {
      case 'tcpServer': await startTcpServer(config as TcpServerConfig); break;
      case 'nmea0183': await connectNmea0183(config as Nmea0183Config); break;
      case 'udpBroadcast': await connectUdpBroadcast(config as UdpBroadcastConfig); break;
      case 'signalk': await connectSignalK(config as SignalKConfig); break;
    }
  };

  // Disconnect and reset to none
  const handleDisconnect = async () => {
    await disconnectAllTransports();
    setActiveTransport('none');
  };

  const transportOptions: Exclude<TransportType, 'none'>[] = ['tcpServer', 'nmea0183', 'udpBroadcast', 'signalk'];

  return (
    <div className="relative">
      {/* Single header button */}
      <button
        className="flex items-center space-x-2 px-3 py-1.5 rounded-md bg-nautical-panel border border-nautical-border hover:bg-nautical-dark transition-colors"
        onClick={() => setShowPanel(!showPanel)}
      >
        <span className={`status-dot ${getHeaderStatusColor()}`} />
        <span className="text-sm text-gray-300">{TRANSPORT_SHORT[activeTransport]}</span>
        {isActive && activeTransport === 'tcpServer' && (
          <span className="text-xs text-gray-500">({tcpServerClientsConnected})</span>
        )}
        {isActive && getMessageCount() > 0 && (
          <span className="text-xs text-gray-500">
            {activeTransport !== 'tcpServer' ? `(${getMessageCount()})` : ''}
          </span>
        )}
        <svg
          className={`w-3 h-3 text-gray-500 transition-transform ${showPanel ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown panel */}
      {showPanel && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-nautical-panel rounded-lg border border-nautical-border shadow-xl z-50">
          <div className="p-4 space-y-3">
            {/* Transport type selector */}
            <div>
              <label className="label mb-1.5">Transport</label>
              <div className="grid grid-cols-4 gap-1">
                {transportOptions.map((t) => (
                  <button
                    key={t}
                    className={`px-2 py-1.5 text-xs rounded transition-colors ${
                      activeTransport === t
                        ? 'bg-blue-600 text-white'
                        : 'bg-nautical-dark text-gray-400 hover:bg-nautical-darker hover:text-gray-200'
                    }`}
                    onClick={() => handleTransportSelect(t)}
                  >
                    {TRANSPORT_SHORT[t]}
                  </button>
                ))}
              </div>
            </div>

            {/* Config form for selected transport */}
            {activeTransport !== 'none' && (
              <>
                <div className="text-xs text-gray-500">
                  {TRANSPORT_DESC[activeTransport]}
                </div>

                {activeTransport === 'tcpServer' && (
                  <TcpServerConfig
                    onConnect={(c) => handleConnect(c)}
                    onDisconnect={handleDisconnect}
                    isActive={isActive}
                  />
                )}
                {activeTransport === 'nmea0183' && (
                  <Nmea0183ConfigForm
                    onConnect={(c) => handleConnect(c)}
                    onDisconnect={handleDisconnect}
                    isActive={isActive}
                  />
                )}
                {activeTransport === 'udpBroadcast' && (
                  <UdpBroadcastConfigForm
                    onConnect={(c) => handleConnect(c)}
                    onDisconnect={handleDisconnect}
                    isActive={isActive}
                  />
                )}
                {activeTransport === 'signalk' && (
                  <SignalKConfigForm
                    onConnect={(c) => handleConnect(c)}
                    onDisconnect={handleDisconnect}
                    isActive={isActive}
                  />
                )}
              </>
            )}

            {activeTransport === 'none' && (
              <div className="text-xs text-gray-500 text-center py-2">
                Select a transport to begin transmitting NMEA data
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
